import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { stopLoaders } from 'src/store/actions/loader';
import {
  useAnnouncementsAPI,
  UseAnnouncementsAPIReturn,
} from './useAnnouncementsAPI';

const SEARCHABLE_FIELDS: string[] = ['title', 'description', 'propertyName'];

export interface IUseAnnouncementsPageOrchestratorReturn {
  announcements: any[];
  isLoadingAnnouncements: boolean;
  searchTerm: string;
  onSearch: (e: any) => void;
  onDeleteAnnouncement: (data: any) => Promise<void>;
  refreshAnnouncements: () => void;
}

const matchesSearch = (announcement: any, term: string): boolean =>
  SEARCHABLE_FIELDS.some((field: string) => {
    const value: any = announcement[field];

    if (value === null || value === undefined) {
      return false;
    }

    return value.toString().toLowerCase().includes(term);
  });

const filterAnnouncements = (
  announcements: any[],
  searchTerm: string
): any[] => {
  const term: string = searchTerm.trim().toLowerCase();

  if (!term) {
    return announcements;
  }

  return announcements.filter((announcement: any) =>
    matchesSearch(announcement, term)
  );
};

export const useAnnouncementsPageOrchestrator =
  (): IUseAnnouncementsPageOrchestratorReturn => {
    const user: any = useSelector(selectAuthenticatedUser);
    const dispatch = useDispatch();

    const {
      announcements,
      isLoadingAnnouncements,
      fetchAnnouncements,
      deleteAnnouncement,
    }: UseAnnouncementsAPIReturn = useAnnouncementsAPI();

    const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string>('');

    useEffect(() => {
      dispatch(stopLoaders());
    }, [dispatch]);

    useEffect(() => {
      if (!user?.id) {
        return;
      }

      const load = async () => {
        await fetchAnnouncements(user.id, user.roles[0].role.name);
        setIsInitialLoading(false);
      };

      void load();
    }, [user?.id]);

    const onSearch = (e: any) => {
      setSearchTerm(e.target.value);
    };

    const onDeleteAnnouncement = async (data: any) => {
      await deleteAnnouncement(data.id);
    };

    const refreshAnnouncements = () => {
      if (!user?.id) {
        return;
      }

      void fetchAnnouncements(user.id, user.roles[0].role.name);
    };

    const visibleAnnouncements: any[] = filterAnnouncements(
      announcements,
      searchTerm
    );

    return {
      announcements: visibleAnnouncements,
      isLoadingAnnouncements: isLoadingAnnouncements || isInitialLoading,
      searchTerm,
      onSearch,
      onDeleteAnnouncement,
      refreshAnnouncements,
    };
  };
